import { useEffect, useState } from 'react'
import { Download, X } from 'lucide-react'
import legalService from '../../services/legalService'
import Button from '../ui/Button'

const APK_DOWNLOAD_URL = '/api/mobile-apk/app-debug.apk'
const APK_DOWNLOAD_FILENAME = 'app-debug.apk'
const TERMS_OF_SERVICE_TYPE = 'terms_of_service'
const TERMS_ACCEPTED_STORAGE_PREFIX = 'login_apk_terms_accepted'

function getTermsAcceptanceKey(versionId) {
  return `${TERMS_ACCEPTED_STORAGE_PREFIX}:${versionId}`
}

function downloadApk() {
  const link = document.createElement('a')
  link.href = APK_DOWNLOAD_URL
  link.download = APK_DOWNLOAD_FILENAME
  link.rel = 'noreferrer'
  document.body.appendChild(link)
  link.click()
  link.remove()
}

export default function ApkDownloadGate() {
  const [termsDialogOpen, setTermsDialogOpen] = useState(false)
  const [termsDocument, setTermsDocument] = useState(null)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [isTermsLoading, setIsTermsLoading] = useState(true)
  const [termsLoadError, setTermsLoadError] = useState('')

  useEffect(() => {
    if (!termsDialogOpen) return undefined

    const originalOverflow = document.body.style.overflow
    const originalPaddingRight = document.body.style.paddingRight
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

    document.body.style.overflow = 'hidden'
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        setTermsDialogOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = originalOverflow
      document.body.style.paddingRight = originalPaddingRight
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [termsDialogOpen])

  useEffect(() => {
    let isMounted = true

    const loadTerms = async () => {
      setIsTermsLoading(true)
      setTermsLoadError('')

      try {
        const documents = await legalService.getCurrentDocuments()
        if (!isMounted) return

        const currentTerms = Array.isArray(documents)
          ? documents.find((document) => document?.type === TERMS_OF_SERVICE_TYPE) || null
          : null

        if (!currentTerms) {
          console.error('Current Terms and Conditions document is missing for APK download gating.')
          setTermsLoadError('Current Terms and Conditions are unavailable right now.')
        }

        setTermsDocument(currentTerms)
      } catch (error) {
        if (!isMounted) return
        console.error('Failed to load current Terms and Conditions for APK download.', error)
        setTermsDocument(null)
        setTermsLoadError('Unable to load Terms and Conditions right now.')
      } finally {
        if (isMounted) {
          setIsTermsLoading(false)
        }
      }
    }

    loadTerms()

    return () => {
      isMounted = false
    }
  }, [])

  const currentTerms = termsDocument?.current || null
  const currentTermsVersionId = String(currentTerms?.id || currentTerms?.version || '').trim()
  const currentTermsAcceptanceKey = currentTermsVersionId ? getTermsAcceptanceKey(currentTermsVersionId) : ''
  const hasAcceptedCurrentTerms = Boolean(
    currentTermsAcceptanceKey && localStorage.getItem(currentTermsAcceptanceKey) === 'accepted',
  )
  const isApkDownloadReady = Boolean(currentTermsVersionId) && !isTermsLoading && !termsLoadError

  const openTermsDialog = () => {
    if (currentTermsVersionId) {
      setTermsAccepted(localStorage.getItem(currentTermsAcceptanceKey) === 'accepted')
    } else {
      setTermsAccepted(false)
    }
    setTermsDialogOpen(true)
  }

  const handleApkLinkClick = (event) => {
    event.preventDefault()

    if (!isApkDownloadReady) {
      return
    }

    if (hasAcceptedCurrentTerms) {
      downloadApk()
      return
    }

    openTermsDialog()
  }

  const handleAcceptTerms = () => {
    if (!currentTermsVersionId || !termsAccepted) {
      return
    }

    localStorage.setItem(currentTermsAcceptanceKey, 'accepted')
    setTermsDialogOpen(false)
    downloadApk()
  }

  const termsStatusText = isTermsLoading
    ? 'Checking current Terms and Conditions...'
    : termsLoadError
      ? termsLoadError
      : hasAcceptedCurrentTerms
        ? 'Terms already accepted for the current version.'
        : 'Accept the current terms before downloading the debug APK.'

  return (
    <>
      <div className="mt-6 rounded-lg border border-outline bg-surface p-4 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-foreground">Pre-release Android APK</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Download the current debug build for testing. Accept the terms before the file downloads.
            </p>
          </div>
          <Download className="mt-0.5 text-blue-600 flex-shrink-0" size={18} />
        </div>
        <a
          href={APK_DOWNLOAD_URL}
          onClick={handleApkLinkClick}
          aria-disabled={!isApkDownloadReady}
          className={`mt-3 inline-flex items-center gap-2 text-sm font-semibold ${
            isApkDownloadReady ? 'text-blue-600 hover:underline' : 'cursor-not-allowed text-muted-foreground opacity-70'
          }`}
        >
          Download app-debug.apk
        </a>
        <p className="mt-2 text-xs text-muted-foreground">{termsStatusText}</p>
        {currentTerms ? (
          <p className="mt-1 text-xs text-muted-foreground">
            Current terms version: {currentTerms.version || currentTerms.id}
          </p>
        ) : null}
      </div>

      {termsDialogOpen && currentTerms && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-black/60 px-4 py-6"
          role="presentation"
          onClick={() => setTermsDialogOpen(false)}
        >
          <div
            className="mx-auto flex min-h-full w-full max-w-4xl items-center justify-center"
            role="presentation"
            onClick={(event) => event.stopPropagation()}
          >
            <div
              data-testid="apk-terms-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="apk-terms-title"
              className="flex w-full max-h-[90vh] flex-col overflow-hidden rounded-2xl border border-outline bg-surface shadow-2xl"
            >
              <div className="flex shrink-0 items-start justify-between gap-4 border-b border-outline/70 px-6 py-5">
                <div>
                  <h2 id="apk-terms-title" className="text-2xl font-bold text-foreground">
                    Terms and Conditions
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Accept the current terms to download the pre-release debug APK.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setTermsDialogOpen(false)}
                  className="rounded-full p-2 text-muted-foreground transition hover:bg-surface-elevated hover:text-foreground"
                  aria-label="Close terms dialog"
                >
                  <X size={18} />
                </button>
              </div>

              <div
                data-testid="apk-terms-scroll"
                className="min-h-0 flex-1 overflow-y-auto px-6 py-5"
              >
                <div className="space-y-4">
                  <div className="rounded-xl border border-outline bg-surface-elevated p-4">
                    <p className="text-sm font-semibold text-foreground">
                      {currentTerms.title || 'Terms and Conditions'}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Version: {currentTerms.version || currentTerms.id}
                    </p>
                    {currentTerms.changeSummary ? (
                      <p className="mt-2 text-xs text-muted-foreground">{currentTerms.changeSummary}</p>
                    ) : null}
                  </div>

                  <div className="rounded-xl border border-outline bg-surface-elevated p-5 text-sm leading-6 text-foreground whitespace-pre-wrap">
                    {currentTerms.contentMarkdown || 'The current terms content is unavailable.'}
                  </div>

                  <label className="flex items-start gap-3 rounded-xl border border-outline bg-surface p-4 text-sm text-foreground">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={termsAccepted}
                      onChange={(event) => setTermsAccepted(event.target.checked)}
                    />
                    <span>I agree to the current Terms and Conditions before downloading the APK.</span>
                  </label>
                </div>
              </div>

              <div className="shrink-0 border-t border-outline/70 bg-surface/95 px-6 py-4 backdrop-blur">
                <div className="flex items-center justify-between gap-4">
                  <p className="text-xs text-muted-foreground">
                    Download starts only after you accept the current version.
                  </p>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setTermsDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleAcceptTerms}
                      disabled={!termsAccepted}
                    >
                      Accept and Download
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
